import { useEffect, useState, type FormEvent } from 'react';
import { ContextMenu, type ContextMenuItem } from '../ContextMenu';
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FileTree,
  Input,
  Label,
  type FileTreeEntry,
} from '@localagent/ui';
import {
  parentPathOf,
  type WorkspaceNodeActions,
  type WorkspaceNameRequest,
} from '../../hooks/useWorkspaceNodeActions';

interface NameCopy {
  title: string;
  description: string;
  label: string;
  confirm: string;
  pending: string;
}

function nameCopyOf(request: WorkspaceNameRequest): NameCopy {
  switch (request.kind) {
    case 'rename':
      return {
        title: 'Rename',
        description: 'Renames the file or folder in place. An existing name is never replaced.',
        label: 'New name',
        confirm: 'Rename',
        pending: 'Renaming…',
      };
    case 'new-file':
      return {
        title: 'New file',
        description:
          request.dirPath === ''
            ? 'Creates the file at the top of the workspace and opens it in the editor.'
            : `Creates the file inside ${request.dirPath} and opens it in the editor.`,
        label: 'File name',
        confirm: 'Create',
        pending: 'Creating…',
      };
    case 'new-folder':
      return {
        title: 'New folder',
        description:
          request.dirPath === ''
            ? 'Creates the folder at the top of the workspace.'
            : `Creates the folder inside ${request.dirPath}.`,
        label: 'Folder name',
        confirm: 'Create',
        pending: 'Creating…',
      };
  }
}

function useDialogSubmit(onClose: () => void) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (op: () => Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await op();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  };
  return { pending, error, run };
}

function DialogError({ error, testId }: { error: string | null; testId: string }) {
  if (error === null) return null;
  return (
    <p className="text-body text-destructive" role="alert" data-testid={testId}>
      {error}
    </p>
  );
}

function NameDialog({
  request,
  onClose,
  onConfirm,
  returnFocus,
}: {
  request: WorkspaceNameRequest;
  returnFocus: (e: Event) => void;
  onClose: () => void;
  onConfirm: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(request.kind === 'rename' ? request.entry.name : '');
  const { pending, error, run } = useDialogSubmit(onClose);
  const copy = nameCopyOf(request);
  const trimmed = name.trim();
  const invalid =
    trimmed.length === 0 ||
    trimmed.includes('/') ||
    (request.kind === 'rename' && trimmed === request.entry.name);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (invalid || pending) return;
    void run(() => onConfirm(trimmed));
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next && !pending) onClose(); }}>
      <DialogContent data-testid="workspace-name-dialog" onCloseAutoFocus={returnFocus}>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="workspace-name-input">{copy.label}</Label>
            <Input
              id="workspace-name-input"
              data-testid="workspace-name-input"
              autoFocus
              value={name}
              disabled={pending}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <DialogError error={error} testId="workspace-name-error" />
          <DialogFooter>
            <Button type="button" variant="ghost" disabled={pending} onClick={() => onClose()}>
              Cancel
            </Button>
            <Button type="submit" disabled={invalid || pending} data-testid="workspace-name-confirm">
              {pending ? copy.pending : copy.confirm}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  entry,
  onClose,
  onConfirm,
  returnFocus,
}: {
  entry: FileTreeEntry;
  returnFocus: (e: Event) => void;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const { pending, error, run } = useDialogSubmit(onClose);
  return (
    <Dialog open onOpenChange={(next) => { if (!next && !pending) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="workspace-delete-dialog" onCloseAutoFocus={returnFocus}>
        <DialogHeader>
          <DialogTitle>{entry.isDir ? 'Delete this folder?' : 'Delete this file?'}</DialogTitle>
          <DialogDescription data-testid="workspace-delete-consequence">
            <span className="block break-all font-mono text-caption">{entry.path}</span>
            {entry.isDir && (
              <span className="mt-2 block">The folder and everything inside it will be deleted.</span>
            )}
            <span className="mt-2 block">This cannot be undone.</span>
          </DialogDescription>
        </DialogHeader>
        <DialogError error={error} testId="workspace-delete-error" />
        <DialogFooter>
          <Button variant="ghost" disabled={pending} onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            data-testid="workspace-delete-confirm"
            disabled={pending}
            onClick={() => void run(onConfirm)}
          >
            {pending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveDialog({
  entry,
  loadChildren,
  onClose,
  onConfirm,
  returnFocus,
}: {
  entry: FileTreeEntry;
  returnFocus: (e: Event) => void;
  loadChildren: (path: string) => Promise<FileTreeEntry[]>;
  onClose: () => void;
  onConfirm: (destDir: string) => Promise<void>;
}) {
  const currentParent = parentPathOf(entry.path);
  const [dest, setDest] = useState(currentParent);
  const [rootDirs, setRootDirs] = useState<FileTreeEntry[] | null>(null);
  const [rootError, setRootError] = useState<string | null>(null);
  const { pending, error, run } = useDialogSubmit(onClose);
  useEffect(() => {
    let live = true;
    loadChildren('')
      .then((entries) => {
        if (live) setRootDirs(entries.filter((e) => e.isDir));
      })
      .catch((e: unknown) => {
        if (live) setRootError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      live = false;
    };
  }, [loadChildren]);

  const intoSelf = entry.isDir && (dest === entry.path || dest.startsWith(`${entry.path}/`));
  const invalid = dest === currentParent || intoSelf;

  return (
    <Dialog open onOpenChange={(next) => { if (!next && !pending) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="workspace-move-dialog" onCloseAutoFocus={returnFocus}>
        <DialogHeader>
          <DialogTitle>Move to…</DialogTitle>
          <DialogDescription>
            Choose the folder to move <span className="font-mono">{entry.name}</span> into. An existing file with
            the same name is never replaced.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1">
          <button
            type="button"
            data-testid="workspace-move-top-level"
            aria-pressed={dest === ''}
            onClick={() => setDest('')}
            className={cn(
              'rounded-sm px-2 py-1 text-left text-body',
              dest === '' ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
            )}
          >
            Top level
          </button>
          <div className="h-56 rounded-sm bg-muted/40" data-testid="workspace-move-picker">
            {rootError !== null ? (
              <p className="p-2 text-body text-destructive" role="alert">
                Could not list folders: {rootError}
              </p>
            ) : rootDirs === null ? (
              <p className="p-2 text-body text-muted-foreground">Loading folders…</p>
            ) : (
              <FileTree
                entries={rootDirs}
                loadChildren={async (dir) => (await loadChildren(dir)).filter((e) => e.isDir)}
                selectedPath={dest === '' ? null : dest}
                defaultExpandedPaths={ancestorsOf(currentParent)}
                onSelectFolder={(folder) => setDest(folder.path)}
              />
            )}
          </div>
          <p className="text-caption text-muted-foreground" data-testid="workspace-move-destination">
            Destination: {dest === '' ? 'top level' : dest}
          </p>
        </div>
        <DialogError error={error} testId="workspace-move-error" />
        <DialogFooter>
          <Button variant="ghost" disabled={pending} onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            data-testid="workspace-move-confirm"
            disabled={invalid || pending}
            onClick={() => void run(() => onConfirm(dest))}
          >
            {pending ? 'Moving…' : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ancestorsOf(dir: string): string[] {
  if (dir === '') return [];
  const parts = dir.split('/');
  return parts.map((_, i) => parts.slice(0, i + 1).join('/'));
}

function menuItemsOf(actions: WorkspaceNodeActions): ContextMenuItem[] {
  const { menu, pending } = actions;
  if (menu === null) return [];
  const { entry } = menu;
  if (entry === null) {
    return [
      { label: 'New file…', disabled: pending, onSelect: () => actions.requestNewFile('') },
      { label: 'New folder…', disabled: pending, onSelect: () => actions.requestNewFolder('') },
    ];
  }
  const items: ContextMenuItem[] = [];
  if (entry.isDir) {
    items.push({ label: 'New file…', disabled: pending, onSelect: () => actions.requestNewFile(entry.path) });
    items.push({ label: 'New folder…', disabled: pending, onSelect: () => actions.requestNewFolder(entry.path) });
  } else {
    items.push({ label: 'Open', onSelect: () => actions.open(entry) });
  }
  items.push({ label: 'Copy path', onSelect: () => actions.copyPath(entry) });
  items.push({ label: 'Rename…', disabled: pending, onSelect: () => actions.requestRename(entry) });
  items.push({ label: 'Move to…', disabled: pending, onSelect: () => actions.requestMove(entry) });
  items.push({ label: 'Delete…', destructive: true, disabled: pending, onSelect: () => actions.requestDelete(entry) });
  return items;
}

export function WorkspaceFileContextMenu({ actions }: { actions: WorkspaceNodeActions }) {
  const { menu, nameRequest, confirmRequest } = actions;
  const returnFocus = (e: Event) => {
    e.preventDefault();
    actions.restoreFocus();
  };
  return (
    <>
      <ContextMenu
        x={menu?.x ?? 0}
        y={menu?.y ?? 0}
        open={menu !== null}
        onClose={actions.closeMenu}
        items={menuItemsOf(actions)}
        label="Workspace file actions"
      />
      {nameRequest !== null && (
        <NameDialog
          request={nameRequest}
          onClose={actions.closeNameRequest}
          onConfirm={actions.confirmName}
          returnFocus={returnFocus}
        />
      )}
      {confirmRequest?.kind === 'delete' && (
        <DeleteDialog
          entry={confirmRequest.entry}
          onClose={actions.closeConfirmRequest}
          onConfirm={actions.confirmDelete}
          returnFocus={returnFocus}
        />
      )}
      {confirmRequest?.kind === 'move' && (
        <MoveDialog
          entry={confirmRequest.entry}
          loadChildren={actions.loadChildren}
          onClose={actions.closeConfirmRequest}
          onConfirm={actions.confirmMove}
          returnFocus={returnFocus}
        />
      )}
    </>
  );
}
